import type { ApprovalAction, ApprovalSummary } from './approvals';
import type { PaymentMethodKind } from './payments';

/**
 * The owner's phone (Phase 7B-3, ADR-033). A tablet sends an approval request it made to the owner's phone; the
 * owner, signed in with an authenticator verified and a fresh authentication, approves or declines it there; the
 * tablet reads the outcome and, once approved, sends its command with the same single-use grant a PIN gives. The
 * owner also gives money back by GCash or bank transfer outside any shift; cash still needs a drawer.
 */

/** Where a request stands. Mirrors app.approval_remote_state. */
export const REMOTE_STATES = [
  'not_asked',
  'waiting',
  'granted',
  'used',
  'grant_expired',
  'declined',
  'withdrawn',
  'expired',
] as const;
export type RemoteState = (typeof REMOTE_STATES)[number];

export interface PersonRef {
  id: string;
  name: string;
}

export interface RemoteStatus {
  state: RemoteState;
  /** Only to the asking tablet, once granted (by a PIN or from the phone). */
  grant_id?: string;
  via?: 'pin' | 'remote';
  expires_at?: string;
  decided_at?: string;
  asked_at?: string;
  asked?: boolean;
  approver?: PersonRef;
  /** Who declined or withdrew it, and the owner's note when declined. */
  by?: PersonRef;
  note?: string | null;
}

/** public.approval_remote_status and public.approval_ask_remote. */
export type RemoteStatusResult =
  | ({ ok: true; request_id?: string } & RemoteStatus)
  | ({ ok: false; code: string } & Partial<RemoteStatus>);

/** One request as the phone shows it (never the grant id or the raw terms). */
export interface RemoteRequest extends RemoteStatus {
  request_id: string;
  action: ApprovalAction;
  permissions: string[];
  subject_type: string;
  subject_id: string;
  order_id: string | null;
  order_number: string | null;
  amount_minor: number | null;
  summary: ApprovalSummary;
  requested_at: string;
  expires_at: string;
  location: { id: string; name: string };
  terminal: { id: string; name: string };
  requested_by: PersonRef;
  own_request: boolean;
}

/** public.remote_approvals. */
export interface RemoteApprovals {
  ok: true;
  server_time: string;
  waiting: RemoteRequest[];
  recent: RemoteRequest[];
}

/** What the tablet does with a status it read. */
export type RemoteStep =
  | { step: 'wait' }
  | { step: 'proceed'; grantId: string }
  | { step: 'stop'; reason: 'declined' | 'withdrawn' | 'expired' | 'used'; note?: string | null };

/**
 * The tablet's next step: wait while the request waits (or was not sent yet), send the command with the grant, or
 * stop with the reason. Whether a grant is still valid is the server's word (`granted` or `grant_expired`), never the
 * tablet's clock; the command is checked again when it arrives.
 */
export function remoteStep(s: RemoteStatus): RemoteStep {
  switch (s.state) {
    case 'granted':
      if (s.grant_id) return { step: 'proceed', grantId: s.grant_id };
      return { step: 'stop', reason: 'expired' };
    case 'grant_expired':
    case 'expired':
      return { step: 'stop', reason: 'expired' };
    case 'used':
      return { step: 'stop', reason: 'used' };
    case 'declined':
      return { step: 'stop', reason: 'declined', note: s.note ?? null };
    case 'withdrawn':
      return { step: 'stop', reason: 'withdrawn' };
    default:
      return { step: 'wait' };
  }
}

/** Whole seconds until `iso`, never below zero (the phone's countdown on a waiting request). */
export function secondsUntil(iso: string, now: Date = new Date()): number {
  return Math.max(0, Math.floor((Date.parse(iso) - now.getTime()) / 1000));
}

/** How often each side reads the other (ms): the tablet waiting on the phone, the phone's list. */
export const REMOTE_POLL_MS = { tablet: 3_000, phone: 5_000 } as const;

/* ── money given back outside a shift ─────────────────────────────────────────────────────────── */

/** A method the owner may give money back by outside a shift: GCash (an e-wallet) or a bank transfer, never cash. */
export function isOutsideShiftMethodKind(kind: PaymentMethodKind): boolean {
  return kind === 'ewallet' || kind === 'bank_transfer';
}

export interface OutsideShiftMethod {
  id: string;
  name: string;
  kind: PaymentMethodKind;
  enabled: boolean;
  archived_at: string | null;
  requires_reference: boolean;
}

/** The methods offered for a refund outside a shift: enabled, not archived, GCash or bank transfer. */
export function outsideShiftMethods<M extends OutsideShiftMethod>(methods: readonly M[]): M[] {
  return methods.filter((m) => m.enabled && !m.archived_at && isOutsideShiftMethodKind(m.kind));
}

export interface OutsideShiftRefundInput {
  amountMinor: number;
  refundableMinor: number;
  method: OutsideShiftMethod | null;
  reference: string;
  reason: string;
}

/** Why the refund cannot be sent yet, or null. The server checks all of it again. */
export function outsideShiftRefundProblem(i: OutsideShiftRefundInput): string | null {
  if (!Number.isInteger(i.amountMinor) || i.amountMinor <= 0)
    return 'Enter the amount to give back.';
  if (i.amountMinor > i.refundableMinor)
    return 'That is more than is left to give back on this order.';
  if (!i.method) return 'Choose how the money goes back.';
  if (i.method.kind === 'cash')
    return 'Cash goes back at a drawer: refund it on the POS tablet, in a shift.';
  if (!isOutsideShiftMethodKind(i.method.kind))
    return 'Outside a shift, money goes back by GCash or bank transfer only.';
  if (i.method.requires_reference && i.reference.replace(/\s+/g, '') === '')
    return `Enter the ${i.method.name} reference of the transfer.`;
  if (i.reason.trim() === '') return 'Say why the money goes back.';
  return null;
}
